import { setAuthenticated } from '@frontend/core/lib/modules/authentication'
import { toast } from 'sonner'
import { authApi, managementApi } from '../servers/api'
import {
  AuthenticatedUser,
  LoginRequestBody,
  LoginResponse,
  UserInOrganisation
} from './types'

type BaseAuthResponse =
  | { success: true }
  | { success: false; error: { message: string; code?: string } }

const authApiWithEndpoints = authApi.injectEndpoints({
  endpoints: (builder) => ({
    // Check Active Session
    login: builder.mutation<LoginResponse, LoginRequestBody>({
      query: (data) => ({
        url: `login`,
        method: 'POST',
        body: {
          ...data,
          returnSecureToken: true
        }
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(setAuthenticated(true))
        } catch (error) {
          console.error('Error during sign-in:', error)
        }
      }
    }),

    // Create Session
    logout: builder.mutation<void, void>({
      query: () => ({
        url: `logout`,
        method: 'GET',
        withCredentials: true
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(setAuthenticated(false))
        } catch (error) {
          console.error('Error during sign-out:', error)
        }
      }
    }),

    refreshToken: builder.query<LoginResponse, void>({
      query: () => `token/refresh`,
      providesTags: ['authToken'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const response = await queryFulfilled

          if (response.data) {
            // Token is valid and refreshed
            dispatch(setAuthenticated(true))
          } else {
            dispatch(setAuthenticated(false))
          }
        } catch (error) {
          console.error('Auth validation failed:', error)
          // Reset authentication state
          dispatch(setAuthenticated(false))
        }
      }
    }),

    // Reset Password Flow
    resetAuthenticatedUserPassword: builder.mutation<BaseAuthResponse, string>({
      query: (email) => ({
        url: `forgotPassword/initiate`,
        method: 'POST',
        body: {
          email
        },
        withCredentials: true
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          console.error('Error resetting user password:', error)
        }
      }
    }),

    // Confirm oobCode for Password (Verify Step)
    verifyPasswordResetCode: builder.mutation<
      BaseAuthResponse,
      { oobCode: string }
    >({
      query: ({ oobCode }) => ({
        url: `forgotPassword/verifyCode`,
        method: 'POST',
        body: {
          oobCode
        }
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          console.error('Error verifying user password reset code:', error)
        }
      }
    }),
    // Update User Password using oobCode
    confirmPasswordReset: builder.mutation<
      BaseAuthResponse,
      { oobCode: string; password: string }
    >({
      query: ({ oobCode, password }) => ({
        url: `forgotPassword/confirm`,
        method: 'POST',
        body: {
          oobCode,
          password
        }
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const response = await queryFulfilled

          if (response.data?.success) {
            dispatch(setAuthenticated(true))
          }
        } catch (error) {
          console.error('Error confirming user password reset:', error)
        }
      }
    }),

    verifyEmail: builder.mutation<BaseAuthResponse, { oobCode: string }>({
      query: ({ oobCode }) => ({
        url: `verifyEmail`,
        method: 'POST',
        body: {
          oobCode
        }
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const response = await queryFulfilled

          if (response.data?.success) {
            dispatch(setAuthenticated(true))
          }
        } catch (error) {
          console.error('Error verifying user email:', error)
        }
      }
    })
  })
})

export const {
  useLoginMutation,
  useLogoutMutation,
  useRefreshTokenQuery,
  useResetAuthenticatedUserPasswordMutation,
  useVerifyPasswordResetCodeMutation,
  useConfirmPasswordResetMutation,
  useVerifyEmailMutation
} = authApiWithEndpoints

// Type for the API response (organisations as array)
interface WhoAmIResponse {
  firstName: string
  lastName: string
  email: string
  image?: string
  organisations: Array<UserInOrganisation>
  globalRole: 'SportableSuperAdmin' | 'Viewer' | 'NoAccess'
}

const authManagementApi = managementApi.injectEndpoints({
  endpoints: (builder) => ({
    whoAmI: builder.query<AuthenticatedUser, void>({
      query: () => `whoami`,
      transformResponse: (response: WhoAmIResponse) => {
        // Transform organisations array to map indexed by id
        const organisationsMap = response.organisations.reduce(
          (acc, org) => {
            acc[org.id] = org
            return acc
          },
          {} as Record<string, (typeof response.organisations)[0]>
        )

        return {
          ...response,
          organisations: organisationsMap
        }
      }
    }),
    resetUserPassword: builder.mutation<null, string>({
      query: (userId) => ({
        url: `/users/${userId}/passwordReset`,
        method: 'POST',
        withCredentials: true
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          toast.success('Reset password email sent successfully')
        } catch (error) {
          toast.error(`There was an error resetting the user's password`)
          console.error('Error resetting user password:', error)
        }
      }
    })
  })
})

export const { useWhoAmIQuery, useResetUserPasswordMutation } =
  authManagementApi
